import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import { InvitationShareDialog } from './InvitationShareDialog'
import { CHARACTERS_ROOT_TAG_ID } from '../../../../shared/tags/canonicalCharacterTags'
import type { TagDefinition } from '../../../../shared/tags/tagRegistry'
import { collectDescendantIds, getTagDisplayPath } from '../../../../shared/tags/tagRegistry'
import { CreateInvitationData, CreatedInvitation, InvitationListItem, apiService } from '../../services/api'

interface TagOption {
  id: string
  name: string
  path: string
}

const getClientUrl = () => window.location.origin

const statusStyles: Record<string, { label: string; dotColor: string }> = {
  pending: { label: 'Pending', dotColor: '#f59e0b' },
  claimed: { label: 'Claimed', dotColor: '#22c55e' },
  expired: { label: 'Expired', dotColor: '#94a3b8' },
}

const cellSx = {
  py: 1.5,
  px: 2,
  fontSize: '0.84rem',
  color: 'var(--text-primary)',
  borderBottom: '1px solid var(--border-subtle)',
}

const headerCellSx = {
  ...cellSx,
  fontSize: '0.72rem',
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  py: 1,
  userSelect: 'none' as const,
}

export interface InvitationManagerProps {
  /** When true, layout fits inside a parent page (full width, no outer maxWidth). */
  embedded?: boolean
  /** Open create dialog prefilled for this character tag (nonce changes each request). */
  tagInviteRequest?: { nonce: number; tagId: string; name: string } | null
  onTagInviteRequestHandled?: () => void
  /** Called after invitations are created, deleted, or reloaded. */
  onInvitationsChanged?: () => void
  /** When set, skips loading invitations from the API (used by People page). */
  invitationsFromParent?: InvitationListItem[]
  /** Hide the invitation list table (People page shows a unified table instead). */
  hideInvitationList?: boolean
  /** The book a character invitation belongs to. Required to invite a character. */
  bookId?: string | null
}

/** Sentinel id for the "create this character" row — no tag id can collide with it. */
const ADD_CHARACTER = '__add_character__'

const filterTagOptions = createFilterOptions<TagOption>()

const InvitationManager: React.FC<InvitationManagerProps> = ({
  embedded = false,
  tagInviteRequest,
  onTagInviteRequestHandled,
  onInvitationsChanged,
  invitationsFromParent,
  hideInvitationList = false,
  bookId,
}) => {
  const [invitations, setInvitations] = useState<InvitationListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [createdInvite, setCreatedInvite] = useState<CreatedInvitation | null>(null)
  const [formData, setFormData] = useState<CreateInvitationData>({
    type: 'regular',
    recipientName: '',
    characterTagId: '',
    characterName: '',
    personalMessage: '',
  })
  const [tags, setTags] = useState<Record<string, TagDefinition>>({})
  const [selectedTag, setSelectedTag] = useState<TagOption | null>(null)
  const [creatingCharacter, setCreatingCharacter] = useState(false)

  /**
   * Add a character to the book from inside the picker. The server creates the tag
   * under the characters root and hands back its id, which goes straight into the
   * form — the author never has to abandon a half-filled invitation for the tag manager.
   */
  const createCharacterTag = useCallback(
    async (name: string) => {
      if (!bookId) {
        return
      }
      setCreatingCharacter(true)
      try {
        const created = await apiService.createCharacter(bookId, name)
        setTags((prev) => ({ ...prev, [created.tagId]: { name: created.name, parentIds: [CHARACTERS_ROOT_TAG_ID] } }))
        setSelectedTag({ id: created.tagId, name: created.name, path: created.name })
        setFormData((prev) => ({ ...prev, characterTagId: created.tagId, characterName: created.name }))
      } catch (err: any) {
        toast.error(err?.message || 'Failed to add the character')
      } finally {
        setCreatingCharacter(false)
      }
    },
    [bookId],
  )

  const tagOptions = useMemo<TagOption[]>(() => {
    const allowed = collectDescendantIds(tags, CHARACTERS_ROOT_TAG_ID)
    return Object.entries(tags)
      .filter(([id]) => allowed.has(id))
      .map(([id, def]) => ({
        id,
        name: def.name,
        path: getTagDisplayPath(tags, id),
      }))
      .sort((a, b) => a.path.localeCompare(b.path, 'he'))
  }, [tags])

  const lastTagInviteNonce = useRef<number | null>(null)

  useEffect(() => {
    if (!tagInviteRequest) {
      lastTagInviteNonce.current = null
      return
    }
    const { nonce, tagId, name } = tagInviteRequest
    if (lastTagInviteNonce.current === nonce) {
      return
    }
    if (Object.keys(tags).length === 0) {
      return
    }
    if (!tags[tagId]) {
      toast.error('Character tag not found')
      lastTagInviteNonce.current = nonce
      onTagInviteRequestHandled?.()
      return
    }
    lastTagInviteNonce.current = nonce
    setFormData((prev) => ({
      ...prev,
      type: 'character',
      characterTagId: tagId,
      characterName: name,
    }))
    setSelectedTag({
      id: tagId,
      name,
      path: getTagDisplayPath(tags, tagId),
    })
    setCreateOpen(true)
    onTagInviteRequestHandled?.()
  }, [tagInviteRequest, tags, onTagInviteRequestHandled])

  useEffect(() => {
    apiService
      .getTheLastManifesto()
      .then((res) => {
        if (res.success && res.data?._id) {
          return apiService.getTagRegistry(res.data._id)
        }
        return null
      })
      .then((res) => {
        if (res?.success) {
          setTags(res.data.tags)
        }
      })
      .catch(() => {})
  }, [])

  const fetchInvitations = useCallback(async () => {
    try {
      const res = await apiService.listInvitations()
      if (res.success) {
        setInvitations(res.data)
      }
    } catch {
      toast.error('Failed to load invitations')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (invitationsFromParent !== undefined) {
      setInvitations(invitationsFromParent)
      setLoading(false)
    } else {
      void fetchInvitations()
    }
  }, [fetchInvitations, invitationsFromParent])

  const handleCreate = async () => {
    if (!formData.recipientName.trim()) {
      toast.error('Recipient name is required')
      return
    }

    try {
      const data: CreateInvitationData = {
        type: formData.type,
        recipientName: formData.recipientName.trim(),
        personalMessage: formData.personalMessage?.trim() || undefined,
      }
      if (formData.type === 'character') {
        if (!bookId) {
          toast.error('Open this from a book before inviting a character')
          return
        }
        data.bookId = bookId
        data.characterTagId = formData.characterTagId || undefined
        data.characterName = formData.characterName?.trim() || undefined
      }

      const res = await apiService.createInvitation(data)
      if (res.success) {
        setCreatedInvite(res.data)
        setCreateOpen(false)
        setFormData({ type: 'regular', recipientName: '', characterTagId: '', characterName: '', personalMessage: '' })
        setSelectedTag(null)
        fetchInvitations()
        onInvitationsChanged?.()
        toast.success('Invitation created')
      }
    } catch {
      toast.error('Failed to create invitation')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await apiService.deleteInvitation(id)
      setInvitations((prev) => prev.filter((inv) => inv.id !== id))
      onInvitationsChanged?.()
      toast.success('Invitation deleted')
    } catch {
      toast.error('Failed to delete invitation')
    }
  }

  const copyLink = (token: string) => {
    const url = `${getClientUrl()}/invite/${token}`
    void navigator.clipboard.writeText(url)
    toast.success('Link copied')
  }

  if (loading && !hideInvitationList) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={28} sx={{ color: 'var(--text-tertiary)' }} />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        maxWidth: embedded ? 'none' : 960,
        mx: embedded ? 0 : 'auto',
        py: embedded ? 0 : 4,
        px: embedded ? 0 : { xs: 2, md: 4 },
      }}
    >
      {hideInvitationList ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 0,
            alignItems: 'center',
            gap: 1,
            width: 'fit-content',
            maxWidth: '100%',
          }}
        >
          {loading && <CircularProgress size={22} sx={{ color: 'var(--text-tertiary)' }} />}
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18, opacity: 0.85 }} />}
            onClick={() => setCreateOpen(true)}
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              px: 1.5,
              borderColor: 'var(--border-subtle)',
              color: 'var(--text-secondary)',
              bgcolor: 'transparent',
              '&:hover': {
                borderColor: 'var(--text-tertiary)',
                bgcolor: 'var(--bg-hover)',
                color: 'var(--text-primary)',
              },
            }}
          >
            New invitation
          </Button>
        </Box>
      ) : (
        <>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: embedded ? 2 : 3 }}>
            <Box>
              <Typography
                sx={{ fontSize: embedded ? '1.1rem' : '1.4rem', fontWeight: 600, color: 'var(--text-primary)' }}
              >
                Invitations
              </Typography>
              <Typography sx={{ fontSize: '0.84rem', color: 'var(--text-tertiary)', mt: 0.25 }}>
                {invitations.length} invitation{invitations.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 500, px: 2.5 }}
            >
              New Invitation
            </Button>
          </Box>

          {/* Table */}
          <Box
            sx={{
              border: '1px solid var(--border-subtle)',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'var(--bg-surface)',
            }}
          >
            {invitations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, px: 3 }}>
                <Typography sx={{ fontSize: '0.9rem', color: 'var(--text-tertiary)' }}>
                  No invitations yet. Create one to invite someone.
                </Typography>
              </Box>
            ) : (
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <Box component="thead">
                  <Box component="tr">
                    <Box component="th" sx={{ ...headerCellSx, textAlign: 'left' }}>
                      Recipient
                    </Box>
                    <Box component="th" sx={{ ...headerCellSx, textAlign: 'left' }}>
                      Type
                    </Box>
                    <Box component="th" sx={{ ...headerCellSx, textAlign: 'left' }}>
                      Status
                    </Box>
                    <Box component="th" sx={{ ...headerCellSx, textAlign: 'left' }}>
                      Date
                    </Box>
                    <Box component="th" sx={{ ...headerCellSx, textAlign: 'left' }}>
                      Claimed By
                    </Box>
                    <Box component="th" sx={{ ...headerCellSx, textAlign: 'right', width: 100 }}>
                      Actions
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {invitations.map((inv) => {
                    const status = statusStyles[inv.status] || statusStyles.pending
                    const claimed = inv.claimedBy
                      ? `${inv.claimedBy.firstName || ''} ${inv.claimedBy.lastName || ''}`.trim() || inv.claimedBy.email
                      : '—'

                    return (
                      <Box
                        component="tr"
                        key={inv.id}
                        sx={{ '&:hover': { bgcolor: 'var(--bg-hover)' }, transition: 'background 0.1s' }}
                      >
                        <Box component="td" sx={cellSx}>
                          <Typography sx={{ fontSize: '0.84rem', fontWeight: 500 }}>{inv.recipientName}</Typography>
                          {inv.characterName && (
                            <Typography sx={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', mt: 0.15 }}>
                              {inv.characterName}
                            </Typography>
                          )}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          {inv.type === 'character' ? 'Character' : 'Regular'}
                        </Box>
                        <Box component="td" sx={cellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                              sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: status.dotColor, flexShrink: 0 }}
                            />
                            {status.label}
                          </Box>
                        </Box>
                        <Box component="td" sx={{ ...cellSx, color: 'var(--text-secondary)' }}>
                          {new Date(inv.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </Box>
                        <Box component="td" sx={{ ...cellSx, color: 'var(--text-secondary)' }}>
                          {claimed}
                        </Box>
                        <Box component="td" sx={{ ...cellSx, textAlign: 'right' }}>
                          <Tooltip title="Copy invite link" arrow>
                            <IconButton
                              size="small"
                              onClick={() => copyLink(inv.token)}
                              sx={{ color: 'var(--icon-default)' }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(inv.id)}
                              sx={{ color: 'var(--text-tertiary)', '&:hover': { color: '#ef5350' } }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>New Invitation</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Type"
            value={formData.type}
            onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value as 'regular' | 'character' }))}
            fullWidth
            margin="normal"
            size="small"
          >
            <MenuItem value="regular">Regular</MenuItem>
            <MenuItem value="character">Character</MenuItem>
          </TextField>

          <TextField
            label="Recipient Name"
            value={formData.recipientName}
            onChange={(e) => setFormData((prev) => ({ ...prev, recipientName: e.target.value }))}
            fullWidth
            required
            margin="normal"
            size="small"
            helperText="The name shown on the landing page"
          />

          {formData.type === 'character' && (
            <Autocomplete
              options={tagOptions}
              value={selectedTag}
              onChange={(_e, val) => {
                if (val?.id === ADD_CHARACTER) {
                  void createCharacterTag(val.name)
                  return
                }
                setSelectedTag(val)
                setFormData((prev) => ({
                  ...prev,
                  characterTagId: val?.id || '',
                  characterName: val?.name || '',
                }))
              }}
              filterOptions={(opts, params) => {
                const filtered = filterTagOptions(opts, params)
                const typed = params.inputValue.trim()
                // Offer to create only a name the book does not already have — otherwise
                // the row invites the author to make a second tag for the same person.
                if (bookId && typed && !opts.some((o) => o.name.trim() === typed)) {
                  filtered.push({ id: ADD_CHARACTER, name: typed, path: `+ Add “${typed}” as a character` })
                }
                return filtered
              }}
              disabled={creatingCharacter}
              getOptionLabel={(opt) => opt.path}
              renderOption={(props, opt) => (
                <li {...props} key={opt.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2">{opt.name}</Typography>
                    {opt.path !== opt.name && (
                      <Typography variant="caption" color="text.secondary">
                        {opt.path}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Character Tag"
                  margin="normal"
                  size="small"
                  helperText="Select the character tag from the book"
                />
              )}
              fullWidth
              size="small"
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
            />
          )}

          <TextField
            label="Personal Message (optional)"
            value={formData.personalMessage}
            onChange={(e) => setFormData((prev) => ({ ...prev, personalMessage: e.target.value }))}
            fullWidth
            multiline
            rows={4}
            margin="normal"
            size="small"
            helperText="Shown to the recipient after they confirm their identity"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none', color: 'var(--text-secondary)' }}>
            Cancel
          </Button>
          <Button onClick={handleCreate} variant="contained" sx={{ textTransform: 'none' }}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <InvitationShareDialog
        open={!!createdInvite}
        onClose={() => setCreatedInvite(null)}
        recipientName={createdInvite?.recipientName ?? ''}
        token={createdInvite?.token ?? ''}
      />
    </Box>
  )
}

export default InvitationManager
